import * as localizationMaps from '../../../localization/maps.js';
import i18n from '../../../localization/i18n';
import tokens from '../../../localization/tokens.js';
import * as Services from '../../../services/index.js';
import * as dates from '@simplybenefits/sb-dates';

/**
 * Parses and formats the initial user data for editing or creation forms.
 * 
 * This function processes the given user data and formats it for use in forms. It adjusts the data based on whether the form
 * is in edit mode or create mode, formats dates, maps certain fields to human-readable formats, and provides default values
 * when necessary.
 * 
 * @param {Object} userData - The user data to be parsed and formatted.
 * @param {boolean} isEdit - Flag indicating if the form is in edit mode.
 * @param {Object} classData - The data containing class information.
 * @param {Object} divisions - The data containing division information.
 * @returns {Object} - The parsed and formatted user data ready for use in the form.
 */
const parseInitialUserData = (userData, isEdit, classData, divisions) => {
  const employeeLanguage = userData?.language === "fr" ? i18n.t(tokens.dropdowns.languages.french) : i18n.t(tokens.dropdowns.languages.english);
  const data = {
    firstname: userData && isEdit ? userData.firstname : '',
    lastname: userData && isEdit ? userData.lastname : '',
    email: userData && isEdit ? userData.email : '',
    birthdate: userData && isEdit ? Services.dates.formatIncomingDate(userData.birthdate) : '',
    phone: userData && isEdit ? userData.phone : '',
    maritlStat: userData && isEdit ? localizationMaps.fromEnglish('maritalStatuses', userData.maritlStat) : '',
    className: userData && isEdit ? classData[userData.classID]?.title : Object.values(classData)[0].title,
    divisionName: userData && isEdit ? divisions[userData.divisionID]?.title : Object.values(divisions)[0].title,
    hireDate: userData && isEdit ? Services.dates.formatIncomingDate(userData.hireDate) : '',
    gender: userData && isEdit ? localizationMaps.fromEnglish("genders", userData.gender) : '',
    title: userData && isEdit ? userData.title : '',
    coverageType: userData && isEdit ? localizationMaps.fromEnglish("coverageTypes", userData.coverageType) : '',
    province: userData && isEdit ? localizationMaps.fromEnglish("provinces", userData.province) : '',
    earningsType: userData && isEdit ? localizationMaps.fromEnglish("earningsTypes", userData.earningsType) : '',
    salary: userData && isEdit ? userData.salary : '',
    hourlyWage: userData && isEdit ? userData.hourlyWage : '',
    hoursPerWeek: userData && isEdit ? userData.hoursPerWeek : '',
    language: employeeLanguage,
    payrollID: userData && isEdit ? userData.payrollID : '',
    waiveEligibilityPeriod: userData && isEdit ? userData.waiveEligibilityPeriod : false
  };
  return data;
};

/**
 * Checks if there are any changes in the user's personal information compared to the initial data.
 * 
 * This function compares the current user data with the initial data to determine if there are any
 * differences in the following fields: firstname, lastname, email, birthdate, phone, and marital status.
 * It returns true if any of these fields have changed, otherwise false.
 * 
 * @param {Object} userData - The current user data.
 * @param {Object} initialData - The initial user data.
 * @returns {boolean} - Returns true if there are changes in the personal information, false otherwise.
 */
const hasPersonalInfoChanges = (userData, initialData) => userData.firstname !== initialData.firstname || userData.lastname !== initialData.lastname || userData.email !== initialData.email || dates.getUTC(userData.birthdate) !== initialData.birthdate || userData.phone !== initialData.phone || localizationMaps.toEnglish('maritalStatuses', userData.maritlStat) !== initialData.maritlStat;

/**
 * Returns the classID for the given class title.
 * 
 * @param {*} classData - Object containing class data
 * @param {*} classTitle - Title of the class
 * @returns {*} classID
 */
const returnClassID = (classData, classTitle) => {
  const classID = Object.values(classData).find(e => e.title === classTitle)?.classID;
  return classID;
};

/**
 * Returns the divisionID for the given division title.
 * 
 * @param {*} divisions - Object containing division data
 * @param {*} divisionTitle - Title of the division
 * @returns {*} divisionID
 */
const returnDivisionID = (divisions, divisionName) => {
  const divisionID = Object.values(divisions).find(e => e.title === divisionName)?.divisionID;
  return divisionID;
};

/**
 * Checks if there are any changes in the user's additional details compared to the initial data.
 * 
 * This function compares the current user data with the initial data to determine if there are any
 * differences in the following fields: divisionName, title, province, earningsType, salary, hourlyWage,
 * hoursPerWeek, language, payrollID, and waiveEligibilityPeriod. It returns an object containing the fields
 * that have changed along with their new values. If no changes are found, it returns an empty object.
 * 
 * @param {Object} userData - The current user data.
 * @param {Object} initialData - The initial user data.
 * @returns {Object} - An object containing the changed fields and their new values, or an empty object if no changes are found.
 */
const returnRegularInfoChanges = (userData, initialData, divisions) => {
  const changes = {};
  if (returnDivisionID(divisions, userData.divisionName) !== initialData.divisionID) {
    changes.divisionID = returnDivisionID(divisions, userData.divisionName);
  }
  if (userData.title !== initialData.title) {
    changes.title = userData.title;
  }
  if (localizationMaps.toEnglish('provinces', userData.province) !== initialData.province) {
    changes.province = localizationMaps.toEnglish('provinces', userData.province);
  }
  if (localizationMaps.toEnglish('genders', userData.gender) !== initialData.gender) {
    changes.gender = localizationMaps.toEnglish('genders', userData.gender);
  }
  if (localizationMaps.toEnglish('earningsTypes', userData.earningsType) !== initialData.earningsType) {
    changes.earningsType = localizationMaps.toEnglish('earningsTypes', userData.earningsType);
  }
  if (Number(userData.salary) !== initialData.salary) {
    changes.salary = Number(userData.salary);
  }
  if (userData.hourlyWage !== initialData.hourlyWage) {
    changes.hourlyWage = userData.hourlyWage;
  }
  if (Number(userData.hoursPerWeek) !== initialData.hoursPerWeek) {
    changes.hoursPerWeek = Number(userData.hoursPerWeek);
  }
  if (localizationMaps.toEnglish('languages', userData.language) === 'English' && initialData.language === 'fr') {
    changes.language = localizationMaps.toEnglish('languages', userData.language);
  }
  if (localizationMaps.toEnglish('languages', userData.language) !== 'English' && initialData.language === 'en') {
    changes.language = localizationMaps.toEnglish('languages', userData.language);
  }
  if (userData.payrollID !== initialData.payrollID) {
    changes.payrollID = userData.payrollID;
  }
  return Object.keys(changes).length ? changes : false;
};

/**
 * Checks if there are any changes in the user's personal information compared to the initial data.
 * 
 * This function compares the current user data with the initial data to determine if there are any
 * differences in the following fields: firstname, lastname, email, birthdate, phone, and marital status.
 * It returns an object containing the fields that have changed along with their new values. If no changes
 * are found, it returns an empty object.
 * 
 * @param {Object} userData - The current user data.
 * @param {Object} initialData - The initial user data.
 * @returns {Object} - An object containing the changed fields and their new values, or an empty object if no changes are found.
 */
const returnPersonalInfoChanges = (userData, initialData) => {
  const changes = {};
  if (userData.firstname !== initialData.firstname) {
    changes.firstname = userData.firstname;
  }
  if (userData.lastname !== initialData.lastname) {
    changes.lastname = userData.lastname;
  }
  if (userData.email !== initialData.email) {
    changes.email = userData.email;
  }
  if (dates.getUTC(userData.birthdate) !== initialData.birthdate) {
    changes.birthdate = dates.getUTC(userData.birthdate);
  }
  if (userData.phone !== initialData.phone) {
    changes.phone = userData.phone;
  }
  if (localizationMaps.toEnglish('maritalStatuses', userData.maritlStat) !== initialData.maritlStat) {
    changes.maritlStat = localizationMaps.toEnglish('maritalStatuses', userData.maritlStat);
  }
  return Object.keys(changes).length ? changes : false;
};

/**
 * Checks if there are any changes in the user's employment details compared to the initial data.
 * 
 * This function compares the current user data with the initial data to determine if there are any
 * differences in the following fields: hireDate and className. It returns an object containing the
 * fields that have changed along with their new values. If no changes are found, it returns an empty object.
 * 
 * @param {Object} userData - The current user data.
 * @param {Object} initialData - The initial user data.
 * @returns {Object} - An object containing the changed fields and their new values, or an empty object if no changes are found.
 */
const returnEmploymentInfoChanges = (userData, initialData, classData) => {
  const changes = {};
  if (dates.getUTC(userData.hireDate) !== initialData.hireDate) {
    changes.hireDate = dates.getUTC(userData.hireDate);
  }
  if (returnClassID(classData, userData.className) !== initialData.classID) {
    changes.classID = returnClassID(classData, userData.className);
  }
  if (userData.waiveEligibilityPeriod !== initialData.waiveEligibilityPeriod) {
    changes.waiveEligibilityPeriod = userData.waiveEligibilityPeriod;
  }
  return Object.keys(changes).length ? changes : false;
};

/**
 * Initial error state matching the deconstructed error state fields
 */
const initialErrorState = {
  firstnameError: false,
  lastnameError: false,
  emailError: false,
  birthdateError: false,
  phoneError: false,
  maritalStatusError: false,
  classNameError: false,
  divisionNameError: false,
  hireDateError: false,
  genderError: false,
  titleError: false,
  coverageTypeError: false,
  provinceError: false,
  earningsTypeError: false,
  salaryError: false,
  hourlyWageError: false,
  hoursPerWeekError: false,
  languageError: false,
  payrollIDError: false,
  waiveEligibilityPeriodError: false,
  employeeDetailsPersonalDetailsError: false
};

/**
 * 
 * @param {*} language 
 * @returns 
 */
const getLanguage = language => language === 'French' || language === 'Français' ? 'fr' : 'en';

/**
 * Parses and prepares user data for an update operation from the admin site, merging updated fields from userData
 * with initialData where applicable, ensuring default values and data consistency.
 * 
 * @param {Object} userData - Updated user data containing fields to update
 * @param {Object} initialData - Initial user data serving as a baseline
 * @returns {Object} Updated user data object with merged fields ready for update
 */
const parseUserDataForUpdate = (userData, initialData, divisions) => {
  const data = {
    email: initialData.email,
    classID: initialData.classID,
    divisionID: returnDivisionID(divisions, userData.divisionName) || initialData.divisionID,
    firstname: initialData.firstname,
    lastname: initialData.lastname,
    birthdate: initialData.birthdate,
    province: userData?.province || initialData.province,
    earningsType: userData?.earningsType || initialData.earningsType,
    salary: userData?.salary || initialData.salary,
    hourlyWage: userData?.hourlyWage || initialData.hourlyWage,
    hoursPerWeek: userData?.hoursPerWeek || initialData.hoursPerWeek,
    language: getLanguage(userData?.language) || getLanguage(initialData.language),
    payrollID: userData?.payrollID || initialData.payrollID || null,
    hireDate: initialData.hireDate,
    gender: userData?.gender || initialData.gender,
    title: userData?.title || initialData.title,
    coverageType: userData?.coverageType || initialData.coverageType || 'Solo'
  };
  return data;
};

/**
 * 
 * @param {*} userData 
 * @returns all parsed user data, for use in adding a new user from the admin and master sites, and editing a user from the master site
 */
const parseUserDataForAddUpdate = (userData, classData, divisions) => {
  const data = {
    email: userData.email,
    classID: returnClassID(classData, userData.className),
    divisionID: returnDivisionID(divisions, userData.divisionName),
    firstname: userData.firstname,
    lastname: userData.lastname,
    birthdate: dates.getUTC(userData.birthdate),
    province: localizationMaps.toEnglish('provinces', userData.province),
    earningsType: localizationMaps.toEnglish('earningsTypes', userData.earningsType),
    salary: Number(userData.salary),
    hourlyWage: Number(userData.hourlyWage),
    hoursPerWeek: Number(userData.hoursPerWeek),
    language: userData.language,
    payrollID: userData.payrollID,
    hireDate: dates.getUTC(userData.hireDate),
    gender: localizationMaps.toEnglish("genders", userData.gender),
    title: userData.title,
    coverageType: localizationMaps.toEnglish('coverageTypes', userData.coverageType),
    waiveEligibilityPeriod: userData.waiveEligibilityPeriod,
    effectiveDate: dates.getUTC()
  };
  return data;
};
export { parseInitialUserData, hasPersonalInfoChanges, returnRegularInfoChanges, returnPersonalInfoChanges, returnEmploymentInfoChanges, initialErrorState, parseUserDataForUpdate, parseUserDataForAddUpdate, returnClassID };